<template>
    <div>
        <div v-if="projectMaster">
            <p class="subtitle">
                ProjectMaster
            </p>
            <h1 class="title">
                {{ projectMaster.Name }}
            </h1>
            <router-link :to="{ name: 'ProjectMasterList' }">
                &laquo; Back to list
            </router-link>
            <b-tabs
                v-model="activeTab"
                position="is-right"
                class="block">
                <b-tab-item label="Overview">
                    <span id="pimsEditor">
                        <div v-if="showPIMSEditor">
                            <code-edit
                                :key="pimsEditKey"
                                :quick-commit="true"
                                :code-set-name="applicationPIMSCodeSetName"
                                :code-id="applicationPIMSViewModel.id"
                                :code-template-values="{
                                    ProjectMaster: projectMaster.Identity,
                                }"
                                :custom-labels="{ name: 'PIMS domain' }"
                                form-title="ApplicationPIMS"
                                library="ApplicationPIMS"
                                @refresh="onSavePIMS"
                                @cancel="cancelPIMS" />
                            <hr>
                        </div>
                    </span>
                    <div v-if="showAdmEditor">
                        <code-edit
                            :quick-commit="true"
                            :code-set-name="admCodeSetName"
                            :code-id="admViewModel.id"
                            :code-template-values="{
                                name: autofillPlaceholder,
                                ProjectMaster: projectMaster.Identity,
                                description: projectMaster.Description,
                            }"
                            form-title="IDM / PM number"
                            library="ApplicationDocumentManagement"
                            :fields-to-disable="['name', 'ProjectMaster']"
                            :before-save-code="beforeSaveAdm"
                            @refresh="onSaveAdm"
                            @cancel="showAdmEditor = false" />
                        <hr>
                    </div>
                    <div class="columns">
                        <div class="column is-4">
                            <nav class="panel is-success">
                                <p class="panel-heading">
                                    Attributes
                                    <a
                                        v-require-can-edit-code="{ libraryName: 'ProjectMaster' }"
                                        class="button is-pulled-right is-small"
                                        @click="gotoEditCode()">
                                        <b-icon
                                            icon="pencil"
                                            size="is-small" />
                                    </a>
                                </p>
                                <div class="panel-block">
                                    <b-table
                                        class="headless-table"
                                        :data="attributesViewModel.attributes"
                                        :striped="true"
                                        :narrowed="true"
                                        :hoverable="false">
                                        <b-table-column
                                            v-slot="props"
                                            field="name"
                                            label="Name">
                                            <b>{{ props.row.name }}</b>
                                        </b-table-column>
                                        <b-table-column
                                            v-slot="props"
                                            field="value"
                                            label="Value">
                                            {{ props.row.value }}
                                            <span v-if="props.row.description"> - {{ props.row.description }}</span>
                                        </b-table-column>
                                    </b-table>
                                </div>
                            </nav>
                        </div>
                        <div class="column is-4">
                            <nav
                                class="panel is-success"
                                style="position: relative">
                                <p class="panel-heading">
                                    Linked Facilities
                                    <a
                                        v-require-can-edit-code="{ libraryName: 'Facility' }"
                                        class="button is-pulled-right is-small"
                                        @click="gotoFacilities()">
                                        <b-icon
                                            icon="pencil"
                                            size="is-small" />
                                    </a>
                                </p>
                                <div>
                                    <b-table
                                        v-if="facilityViewModel"
                                        :data="facilityViewModel.referenced"
                                        default-sort="name"
                                        :striped="false"
                                        :narrowed="true"
                                        :hoverable="false">
                                        <b-table-column
                                            v-slot="props"
                                            field="name"
                                            label="Name">
                                            {{ props.row.Name }}
                                        </b-table-column>
                                        <b-table-column
                                            v-slot="props"
                                            field="SAPPlant"
                                            label="SAP Plant">
                                            {{ props.row.SAPPlant }}
                                        </b-table-column>
                                        <b-table-column
                                            v-slot="props"
                                            field="STIDDB"
                                            label="STID DB">
                                            {{ props.row.STIDDB }}
                                        </b-table-column>
                                        <b-table-column
                                            v-slot="props"
                                            field="description"
                                            label="Description">
                                            {{ props.row.Description }}
                                        </b-table-column>
                                    </b-table>
                                </div>
                                <b-loading
                                    v-model="loadingFacilities"
                                    :is-full-page="false"
                                    :can-cancel="false" />
                            </nav>
                            <nav
                                style="position: relative"
                                class="panel is-success">
                                <p class="panel-heading">
                                    Linked Projects
                                    <a
                                        v-require-can-edit-code="{ libraryName: 'Project' }"
                                        class="button is-pulled-right is-small"
                                        @click="gotoProjects()">
                                        <b-icon
                                            icon="pencil"
                                            size="is-small" />
                                    </a>
                                </p>
                                <div class="panel-block">
                                    <b-table
                                        v-if="projectViewModel"
                                        class="headless-table"
                                        :data="projectViewModel.referenced"
                                        default-sort="Name"
                                        :striped="false"
                                        :narrowed="true"
                                        :hoverable="false">
                                        <b-table-column
                                            v-slot="props"
                                            field="Name"
                                            label="Name">
                                            {{ props.row.Name }}
                                        </b-table-column>
                                        <b-table-column
                                            v-slot="props"
                                            field="Description"
                                            label="Description">
                                            {{ props.row.Description }}
                                        </b-table-column>
                                    </b-table>
                                </div>
                                <b-loading
                                    v-model="loadingProjectMasterProjects"
                                    :is-full-page="false"
                                    :can-cancel="false" />
                            </nav>
                            <contracts-box
                                v-if="projectMaster"
                                ref="contractsBox"
                                :code-id="projectMaster.Identity"
                                @go-to-contract="gotoContracts" />
                        </div>
                        <div class="column is-4">
                            <nav
                                class="panel is-success"
                                style="position: relative">
                                <p class="panel-heading">
                                    PIMS ST (Risk, Quality, Project Control, Management of Change)
                                    <a
                                        v-require-can-edit-code="{ libraryName: 'ApplicationPIMS' }"
                                        class="button is-pulled-right is-small"
                                        @click="editPIMS()">
                                        <b-icon
                                            :icon="pimsIconDisplayed"
                                            size="is-small" />
                                    </a>
                                </p>
                                <div class="panel-block">
                                    <b-table
                                        class="headless-table"
                                        :data="applicationPIMSViewModel.properties"
                                        default-sort="name"
                                        :striped="false"
                                        :narrowed="true"
                                        :hoverable="false">
                                        <b-table-column
                                            v-slot="props"
                                            field="name"
                                            label="Name">
                                            {{ props.row.name }}:
                                        </b-table-column>
                                        <b-table-column
                                            v-slot="props"
                                            field="value"
                                            label="Value">
                                            {{ props.row.value }}
                                        </b-table-column>
                                    </b-table>
                                    <b-loading
                                        v-model="loadingApplicationPIMS"
                                        :is-full-page="false"
                                        :can-cancel="false" />
                                </div>
                            </nav>
                            <adm-panel
                                :key="admKey"
                                :project-master="projectMaster.Identity"
                                @editAdm="editAdm" />
                            <nav
                                class="panel is-success"
                                style="position: relative">
                                <p class="panel-heading">
                                    ProCoSys
                                </p>
                                <div class="panel-block">
                                    <b-table
                                        v-if="applicationProCoSysViewModel"
                                        class="headless-table"
                                        :data="applicationProCoSysViewModel"
                                        default-sort="facility"
                                        :striped="false"
                                        :narrowed="true"
                                        :hoverable="false">
                                        <b-table-column
                                            v-slot="props"
                                            field="name"
                                            label="Name">
                                            {{ props.row.facility }}
                                        </b-table-column>
                                        <b-table-column
                                            v-slot="props"
                                            field="description"
                                            label="Description">
                                            {{ props.row.projectSchema }}
                                        </b-table-column>
                                    </b-table>
                                </div>
                                <b-loading
                                    v-model="loadingProjectMasterProjects"
                                    :is-full-page="false"
                                    :can-cancel="false" />
                            </nav>
                        </div>
                    </div>
                </b-tab-item>

                <b-tab-item
                    label="Edit attributes">
                    <modal-copy-plan-project-attributes
                        v-if="showPlanProjectAttributesModal"
                        :plan-project="codeEditLocalPlanProject"
                        :project-master="codeEditLocalCode"
                        :attributes="codeEditPmPpAttrDelta"
                        @onCancel="togglePlanProjectAttrsModal"
                        @onConfirm="handleConfirmPlanProjectAttrs" />
                    <code-edit
                        :key="projectMasterCodeEditKey"
                        :quick-commit="true"
                        :code-id="codeId"
                        :fields-to-disable="['CLID','CVPID']"
                        :referable-codes-filters="attributeRefCodeFilter(planProjectUsageCounts, projectMaster.PlanProject)"
                        :referable-codes-validators="attributeRefCodeValidator(planProjectUsageCounts)"
                        :ref-code-changed-handlers="refCodeChangedHandlers(planProjectUsageCounts)"
                        library="ProjectMaster"
                        @cancel="gotoOverview"
                        @refresh="refreshProjectMaster" />
                </b-tab-item>
                <b-tab-item
                    v-require-can-edit-code="{ libraryName: 'Project' }"
                    label="Projects">
                    <div style="margin-bottom: 10px">
                        <div class="control has-icons-left">
                            <input
                                v-model="projectFilterText"
                                type="text"
                                class="input"
                                placeholder="Search available by Name (minimum 3 characters)">
                            <span class="icon is-small is-left">
                                <i class="mdi mdi-magnify" />
                            </span>
                        </div>
                        <b-loading
                            v-model="loadingProjectSearch"
                            :is-full-page="false"
                            :can-cancel="false" />
                    </div>
                    <set-selector
                        v-if="projectViewModel"
                        :key="projectSetSelectorKey"
                        :identity="elementIdentity"
                        :initial-elements="projectSearch"
                        :sort-by="['element.Name']"
                        :initial-selection="projectViewModel.referenced"
                        available-elements-title="Available projects"
                        selected-elements-title="Selected projects"
                        @save="projectRelationsSave">
                        <template #left-item="props">
                            <div
                                class="notification is-light"
                                style="margin-bottom: 2px">
                                <span><b>{{ props.element.Name }}</b>
                                    {{ props.element.Description }}</span>
                            </div>
                        </template>
                        <template #right-item="props">
                            <div
                                class="notification is-light"
                                style="margin-bottom: 2px">
                                <span><b>{{ props.element.Name }}</b>
                                    {{ props.element.Description }}</span>
                            </div>
                        </template>
                    </set-selector>
                </b-tab-item>
                <b-tab-item
                    v-require-can-edit-code="{ libraryName: 'Facility' }"
                    label="Facilities">
                    <div style="margin-bottom: 10px">
                        <div class="control has-icons-left">
                            <input
                                v-model="facilityFilterText"
                                type="text"
                                class="input"
                                placeholder="Filter available facilities">
                            <span class="icon is-small is-left">
                                <i class="mdi mdi-magnify" />
                            </span>
                        </div>
                    </div>
                    <set-selector
                        v-if="facilityViewModel"
                        :key="facilitySetSelectorKey"
                        :identity="elementIdentity"
                        :sort-by="['element.Name']"
                        :initial-elements="facilityViewModel.available"
                        :initial-selection="facilityViewModel.referenced"
                        :filter="facilityFilter"
                        available-elements-title="Available facilities"
                        selected-elements-title="Selected facilities"
                        @save="facilityRelationsSave">
                        <template #left-item="props">
                            <div
                                class="notification is-light"
                                style="margin-bottom: 2px">
                                <span><b>{{ props.element.Name }}</b>
                                    {{ props.element.Description }}</span>
                            </div>
                        </template>
                        <template #right-item="props">
                            <div
                                class="notification is-light"
                                style="margin-bottom: 2px">
                                <span><b>{{ props.element.Name }}</b>
                                    {{ props.element.Description }}</span>
                            </div>
                        </template>
                    </set-selector>
                </b-tab-item>
                <b-tab-item
                    v-require-can-edit-code="{ libraryName: 'Contract' }"
                    label="Contracts">
                    <contracts-tab
                        :project-master="projectMaster"
                        @refresh="onContractsSaved" />
                </b-tab-item>
            </b-tabs>
        </div>
    </div>
</template>

<script>
    import _ from 'lodash';
    import ContractsBox from './ContractsBox.vue';
    import ContractsTab from './ContractsTab.vue';
    import CodeEdit from '@/shared/codeedit/CodeEdit.vue';
    import SetSelector from '@/shared/components/SetSelector.vue';
    import AdmPanel from './AdmPanel.vue';
    import { showMixin } from '@/shared/mixins/showMixin';
    import { createChangeDocAndCommit, genericViewQueryAsText, getMetadata, sqlQuery, getCodeByIdWithReferences } from '@/shared/helpers/api';
    import { decodeIdBase64, encodeIdBase64 } from '@/shared/helpers/utils';
    import { http } from '@/shared/httpWrapper.js';
    import { requireCanEditCode } from '@/shared/directives/requirePermission';
    import {
        attributeRefCodeFilter,
        attributeRefCodeValidator,
        loadPlanProjectReferenceCount,
        PlanProjectProjectMasterAttributeDelta
    } from '@/apps/facilityAndProject/ProjectMaster.helper';
    import ModalCopyPlanProjectAttributes from '@/apps/facilityAndProject/ModalCopyPlanProjectAttributes.vue';

    // TODO "loading"

    export default {
        directives: {
            'require-can-edit-code': requireCanEditCode
        },
        components: {
            ModalCopyPlanProjectAttributes,
            CodeEdit,
            SetSelector,
            ContractsBox,
            ContractsTab,
            AdmPanel
        },
        mixins: [showMixin],
        props: {
            id: {
                default: null,
                type: String
            }
        },
        data() {
            return {
                activeTab: 0,
                applicationPIMS: [],
                applicationPIMSAttributeDefinitions: [],
                applicationPIMSCodeSetName: null,
                applicationProCoSys: [],
                codeEditLocalCode: null,
                codeEditLocalPlanProject: null,
                codeEditPmPpAttrDelta: [],
                facilityFilter: (x) => x,
                facilityFilterText: '',
                facilitySearch: [],
                facilityProjectMasterCodeSetName: null,
                facilitySetSelectorKey: 1,
                loadingApplicationPIMS: false,
                loadingApplicationProCoSys: false,
                loadingFacilities: false,
                loadingProjectMasterProjects: false,
                loadingProjectSearch: false,
                pimsEditKey: 1,
                projectAccessGroups: [],
                projectCodeSetName: null,
                projectFilterText: '',
                projectMaster: null,
                projectMasterAttributeDefinitions: [],
                projectMasterCodeEditKey: 1,
                projectMasterNameHistory: null, // TODO rename
                projectMasterProjects: [],
                planProjectUsageCounts: null,
                projectSearch: [],
                projectSetSelectorKey: 1,
                referencedFacilities: [],
                showPIMSEditor: false,
                admCodeSetName: null,
                showAdmEditor: false,
                showPlanProjectAttributesModal: false,
                admViewModel: null,
                admKey: 1,
                autofillPlaceholder: 'Auto-assigned on save'
            };
        },
        computed: {
            codeId() {
                if (this.projectMaster) {
                    return encodeIdBase64('Code', this.projectMaster.Id);
                }
                return null;
            },
            attributesViewModel() {
                const self = this;
                if (this.projectMaster) {
                    const attrs = _.sortBy(
                        this.projectMasterAttributeDefinitions,
                        (x) => x.sequenceNumber
                    ).map(function(x) {
                        const a = { name: x.displayAs, value: self.projectMaster[x.name] };
                        if (x.attributeType === 'CodeRef') {
                            const codeRefNameProperty = x.name + '-Name';
                            const codeRefDescriptionProperty = x.name + '-Description';
                            a.value = self.projectMaster[codeRefNameProperty];
                            if (self.projectMaster[codeRefNameProperty] !== self.projectMaster[codeRefDescriptionProperty]) {
                                a.description = self.projectMaster[codeRefDescriptionProperty];
                            }
                        }
                        return a;
                    });
                    attrs.unshift({
                        name: 'IsValid',
                        value: this.projectMaster.IsValid ? 'True' : 'False'
                    });
                    attrs.unshift({
                        name: 'Description',
                        value: this.projectMaster.Description
                    });
                    return {
                        attributes: attrs
                    };
                }
                return {
                    attributes: []
                };
            },
            facilityViewModel() {
                if (this.projectMaster && !this.loadingFacilities) {
                    return {
                        available: this.facilitySearch,
                        referenced: this.referencedFacilities
                    };
                }
                return {
                    available: [],
                    referenced: []
                };
            },
            projectViewModel() {
                if (this.projectMaster && this.projectMasterProjects) {
                    return {
                        referenced: this.projectMasterProjects
                    };
                }
                return {
                    referenced: []
                };
            },
            applicationPIMSViewModel() {
                if (this.projectMaster && this.applicationPIMS.length > 0) {
                    if (this.applicationPIMS.length > 1) {
                        return [
                            {
                                name: 'ERROR',
                                value: 'Found multiple PIMS linked'
                            }
                        ];
                    }
                    const linked = this.applicationPIMS[0];
                    const model = [
                        { name: 'PIMS domain', value: linked.Name },
                        { name: 'CommonLib description', value: linked.Description },
                        { name: 'PIMS ST description', value: linked.PIMSDescription },
                        { name: 'Is archived', value: linked.IsArchived }
                    ];
                    return {
                        id: encodeIdBase64('Code', linked.Id),
                        properties: model
                    };
                }
                return {
                    properties: []
                };
            },
            applicationProCoSysViewModel() {
                if (this.referencedFacilities.length > 0 && this.applicationProCoSys) {
                    const facilities = this.facilityViewModel.referenced;
                    if (facilities) {
                        const proCoSysList = this.applicationProCoSys;
                        return facilities.map(f => {
                            const found = _.find(proCoSysList, (p) => p.Facility === f.Identity);
                            return {
                                facility: f.Identity,
                                projectSchema: found ? found.ProjectSchema : '**missing**'
                            };
                        });
                    }
                }
                return [];
            },
            pimsIconDisplayed() {
                const apvm = this.applicationPIMSViewModel;
                const showAddSymbol = apvm.properties.length === 0;
                return showAddSymbol ? 'plus-thick' : 'pencil';
            }
        },
        watch: {
            async projectFilterText() {
                await this.loadProjects();
            },
            facilityFilterText(value) {
                if (value === '') this.facilityFilter = (x) => x;

                const parts = value.toLowerCase().split(' ');
                this.facilityFilter = function(x) {
                    const tmp = (x.Name + x.Description).toLowerCase();
                    return _.every(parts, function(p) {
                        return tmp.indexOf(p) > -1;
                    });
                };
            },
            projectViewModel(value) {
                if (value) {
                    this.projectSetSelectorKey += 1;
                }
            },
            facilityViewModel(value) {
                if (value) {
                    this.facilitySetSelectorKey += 1;
                }
            },
            applicationPIMSViewModel(value) {
                if (value) {
                    this.pimsEditKey += 1;
                }
            }
        },
        async mounted() {
            const metadataRequest = [
                {
                    request: 'code-sets',
                    values: ['ApplicationPIMS', 'FacilityProjectMaster', 'Project', 'ProjectMasterNameHistory', 'ApplicationDocumentManagement']
                },
                {
                    request: 'attribute-definitions',
                    values: ['ApplicationPIMS', 'ProjectMaster']
                }
            ];
            const metadataResponse = await getMetadata(this, metadataRequest);
            const metadata = metadataResponse.data;

            this.applicationPIMSCodeSetName = metadata['code-sets']['ApplicationPIMS'][0].name;
            this.facilityProjectMasterCodeSetName = metadata['code-sets']['FacilityProjectMaster'][0].name;
            this.projectCodeSetName = metadata['code-sets']['Project'][0].name;
            this.projectMasterNameHistory = metadata['code-sets']['ProjectMasterNameHistory'][0].name;
            this.admCodeSetName = metadata['code-sets']['ApplicationDocumentManagement'][0].name;

            this.applicationPIMSAttributeDefinitions = metadata['attribute-definitions']['ApplicationPIMS'].attributeDefinitions;
            this.projectMasterAttributeDefinitions = metadata['attribute-definitions']['ProjectMaster'].attributeDefinitions;

            await this.loadProjectMaster();

            [, , , , this.planProjectUsageCounts] = await Promise.all([
                this.loadProjectMasterProjects(),
                this.loadApplicationPIMS(),
                this.facilityLoader(),
                this.loadApplicationProCoSys(),
                loadPlanProjectReferenceCount()
            ]);
        },
        methods: {
            attributeRefCodeValidator,
            attributeRefCodeFilter,
            async loadProjectMaster() {
                this.projectMaster = (await getCodeByIdWithReferences(this, 'ProjectMaster', decodeIdBase64(this.id)))[0];
            },
            async loadProjectMasterProjects() {
                this.loadingProjectMasterProjects = true;
                this.projectMasterProjects = (await genericViewQueryAsText(
                    this,
                    `FROM Project
                    WHERE ProjectMaster = @pmIdentity
                    AND IsValid = true`,
                    [{ name: '@pmIdentity', value: this.projectMaster.Identity }]
                )).data;
                this.loadingProjectMasterProjects = false;
            },
            async loadProjects() {
                if (this.projectFilterText.length >= 3) {
                    this.loadingProjectSearch = true;
                    this.projectSearch = (await genericViewQueryAsText(
                        this,
                        `FROM Project
                         WHERE ProjectMaster IS NULL
                         AND Name LIKE @nameFilter
                         AND IsValid=true`,
                        [{
                            name: '@nameFilter',
                            value: `%${this.projectFilterText.trim()}%`
                        }]
                    )).data;
                    this.loadingProjectSearch = false;
                }
            },
            async loadApplicationPIMS() {
                this.loadingApplicationPIMS = true;
                this.applicationPIMS = (await genericViewQueryAsText(
                    this,
                    `FROM ApplicationPIMS
                     WHERE IsValid=true
                     AND ProjectMaster = @masterIdentity`,
                    [{ name: '@masterIdentity', value: this.projectMaster.Identity }]
                )).data;
                this.loadingApplicationPIMS = false;
            },
            async loadApplicationProCoSys() {
                this.loadingApplicationProCoSys = true;
                this.applicationProCoSys = (await genericViewQueryAsText(
                    this,
                    'FROM ApplicationProCoSys WHERE IsValid=true'
                )).data;
                this.loadingApplicationProCoSys = false;
            },
            async loadFacilityRelations() {
                this.referencedFacilities = (await genericViewQueryAsText(this, `
                    FROM FacilityProjectMaster
                    WHERE IsValid = true
                    JOIN Facility
                        SELECT Identity, Name, Description, SAPPlant, STIDDB
                        WHERE IsValid = true
                    END
                    JOIN ProjectMaster
                        WHERE Identity = @masterIdentity AND IsValid = true
                    END
                `, [{ name: '@masterIdentity', value: this.projectMaster.Identity }])).data;
            },
            async loadFacilities() {
                this.facilitySearch = (await genericViewQueryAsText(
                    this,
                    `FROM Facility
                     SELECT Name, Identity, Description
                     WHERE IsValid = true`
                )).data.filter(f => !this.referencedFacilities.some(rf => rf.Name === f.Name));
            },
            // These need to be loaded in correct order, so they are wrapped in helping function
            async facilityLoader() {
                this.loadingFacilities = true;
                await this.loadFacilityRelations();
                await this.loadFacilities();
                this.loadingFacilities = false;
            },
            onContractsSaved() {
                this.$refs.contractsBox.refresh();

                // Force Attributes code-edit to re-mount so it picks up the changed contract relations
                this.projectMasterCodeEditKey += 1;
            },
            elementId(e) {
                return e.id;
            },
            elementIdentity(e) {
                return e.identity;
            },
            gotoOverview() {
                this.activeTab = 0;
            },
            gotoEditCode() {
                this.activeTab = 1;
            },
            gotoProjects() {
                this.activeTab = 2;
            },
            gotoFacilities() {
                this.activeTab = 3;
            },
            gotoContracts() {
                this.activeTab = 4;
            },
            editPIMS() {
                if (!this.showAdmEditor)
                    this.showPIMSEditor = true;
            },
            editAdm(admViewModel) {
                if (!this.showPIMSEditor) {
                    this.showAdmEditor = true;
                    this.admViewModel = admViewModel;
                }
            },
            cancelPIMS() {
                this.showPIMSEditor = false;
            },
            onSavePIMS() {
                this.showPIMSEditor = false;
                this.loadApplicationPIMS();
            },
            async beforeSaveAdm(code) {
                if (code['name'] === this.autofillPlaceholder) {
                    const prevNo = await this.getPmSequenceNo();
                    code['name'] = 'PM' + String(prevNo + 1).padStart(4, '0');
                }
            },
            async getPmSequenceNo() {
                const result = await sqlQuery(http, {
                    query: `SELECT TOP(1) Name
                        FROM ApplicationDocumentManagement
                        WHERE (Name LIKE 'PM[0-9][0-9][0-9][0-9]'
                            OR Name LIKE 'PM[0-9][0-9][0-9][0-9][0-9]'
                            OR Name LIKE 'PM[0-9][0-9][0-9][0-9][0-9][0-9]')
                            AND LEN(Name) = (SELECT MAX(LEN(Name))
                                           FROM ApplicationDocumentManagement
                                           WHERE Name LIKE 'PM[0-9][0-9][0-9][0-9]'
                                              OR Name LIKE 'PM[0-9][0-9][0-9][0-9][0-9]'
                                              OR Name LIKE 'PM[0-9][0-9][0-9][0-9][0-9][0-9]')
                        ORDER BY Name DESC`
                });
                if (result.data.length === 0) {
                    return 0;
                } else {
                    return Number(result.data[0].Name.substring(2));
                }
            },
            onSaveAdm() {
                this.showAdmEditor = false;
                this.admKey++;
            },
            async refreshProjectMaster(codes) {
                if (
                    codes
                    && codes.length > 0
                    && codes[0].Name !== this.projectMaster.Name
                ) {
                    const historyChange = [
                        {
                            Name: codes[0].Name,
                            Description: '',
                            IsValid: 'true',
                            ProjectMaster: this.projectMaster.Identity,
                            OldName: this.projectMaster.Name,
                            NameChangeDecisionDate: new Date().toJSON()
                        }
                    ];

                    await createChangeDocAndCommit(
                        this,
                        this.projectMasterNameHistory,
                        historyChange
                    );
                }

                await this.loadProjectMaster();
                this.planProjectUsageCounts = await loadPlanProjectReferenceCount();

                // Force Attributes code-edit to re-mount
                this.projectMasterCodeEditKey += 1;
            },
            async projectRelationsSave(state) {
                try {
                    const changes = _.concat(
                        state.added.map((project) => ({
                            Name: project.Name,
                            ProjectMaster: this.projectMaster.Identity
                        })),
                        state.removed.map((project) => ({
                            Name: project.Name,
                            ProjectMaster: '{null}'
                        }))
                    );
                    const res = await createChangeDocAndCommit(
                        this,
                        this.projectCodeSetName,
                        changes
                    );
                    this.showInfo(res.data.message);
                    await this.loadProjectMasterProjects();
                    await this.loadProjects();
                } catch (err) {
                    this.showError(err);
                }
            },
            async facilityRelationsSave(state) {
                try {
                    const changes = _.concat(
                        state.added.map(
                            this.facilityChangeConstructor(this.projectMaster, true)
                        ),
                        state.removed.map(
                            this.facilityChangeConstructor(
                                this.projectMaster,
                                false
                            )
                        )
                    );
                    const res = await createChangeDocAndCommit(
                        this,
                        this.facilityProjectMasterCodeSetName,
                        changes
                    );
                    this.showInfo(res.data.message);
                    await this.facilityLoader();
                } catch (err) {
                    this.showError(err);
                }
            },
            facilityChangeConstructor(projectMaster, isValid) {
                return function(facility) {
                    const tmp = `${facility.Name} rel ${projectMaster.Name}`;
                    return {
                        Facility: facility.Identity,
                        ProjectMaster: projectMaster.Identity,
                        Name: tmp,
                        Description: tmp,
                        IsValid: isValid
                    };
                };
            },
            togglePlanProjectAttrsModal() {
                this.showPlanProjectAttributesModal = !this.showPlanProjectAttributesModal;
            },
            refCodeChangedHandlers(planProjects) {
                return {
                    PlanProject: (local) => {
                        const pp = planProjects.find((x) => x.Identity === local.PlanProject);
                        if (!pp) {
                            return;
                        }
                        this.codeEditLocalPlanProject = pp;
                        this.codeEditLocalCode = local;
                        this.codeEditPmPpAttrDelta = PlanProjectProjectMasterAttributeDelta(
                            ['ProfitCenter', 'Phase', 'Country'],
                            local,
                            pp
                        );
                        if (this.codeEditPmPpAttrDelta.length > 0) {
                            this.togglePlanProjectAttrsModal();
                        }
                    }
                };
            },
            handleConfirmPlanProjectAttrs(valuesToCopy) {
                this.togglePlanProjectAttrsModal();
                valuesToCopy.map((attr) => {
                    this.codeEditLocalCode[attr] = this.codeEditLocalPlanProject[attr];
                });
            }

        }
    };
</script>

<style>
.headless-table thead {
    display: none;
}

.headless-table {
    width: 100%;
}
</style>
